'use client';

import { QueryKey, useMutation, useQueryClient } from '@tanstack/react-query';
import { SocialReaction } from 'types/src/types';
import { toggleSocialCommentReaction } from '@/src/actions/social-comments';

type Vars = {
  commentId: number;
  reaction: SocialReaction;
  queryKey: QueryKey;
};

function patchInfiniteCommentById(
  data: any,
  commentId: number,
  patch: (c: any) => any
) {
  if (!data?.pages) return data;

  return {
    ...data,
    pages: data.pages.map((page: any) => {
      const list = page?.data?.comments;
      if (Array.isArray(list)) {
        return {
          ...page,
          data: {
            ...page.data,
            comments: list.map((c: any) =>
              c?.id === commentId ? patch(c) : c
            ),
          },
        };
      }
      return page;
    }),
  };
}

function applyServer(c: any, serverData: any) {
  const myReaction = serverData?.myReaction ?? null;
  const counts = serverData?.counts ?? null;

  return {
    ...c,
    myReaction,
    reactionCounts: counts ?? c.reactionCounts,
  };
}

export function useToggleCommentReactionMutation() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ commentId, reaction }: Vars) => {
      const res = await toggleSocialCommentReaction(commentId, reaction);
      if (res.error) throw new Error(res.error);
      return res.data;
    },

    onSuccess: (serverData, { commentId, queryKey }) => {
      qc.setQueryData(queryKey, (old: any) =>
        patchInfiniteCommentById(old, commentId, (c) =>
          applyServer(c, serverData)
        )
      );
    },

    onSettled: (_data, _err, { queryKey }) => {
      qc.invalidateQueries({ queryKey });
    },
  });
}
